import moment from 'moment';
import { FlyerCardThumbnail } from '@/graphql/types';
import { FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import { postMetrics, postOne } from '@/utils/metricsRequests';
import { useReactiveVar } from '@apollo/client';
import { userInformationVar } from '@/graphql/state';
import { useURLSearchParams } from './useURLSearchParams';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

type Track = {
  metric: string;
  action?: string;
  featureId?: string | null;
  flyerId: string;
  resource: string;
  resourceId: string;
  audienceId?: string | null;
};

type Impression = {
  flyers: FlyerCardThumbnail[];
  flyerType: string;
  districtId: string;
  audienceId?: string;
};

type Metrics = {
  trackMetrics: (args: Track) => void;
  trackImpressions: (args: Impression) => void;
};

export const METRICS_SOURCE_NAME = 'parent-app';
export const ORIGIN_PEACHJAR = 'peachjar';

export const useMetrics = (): Metrics => {
  const user = useReactiveVar(userInformationVar);
  const { audienceType } = useURLSearchParams();
  const { search } = useLocation();
  const [origin, setOrigin] = useState<string>(ORIGIN_PEACHJAR);
  const searchParams = new URLSearchParams(search);
  const originParam = searchParams.get('origin');

  const updateOrigin = () => {
    const hasTracked = sessionStorage.getItem('user_session');
    if (!hasTracked && originParam) {
      sessionStorage.setItem('user_session', 'on');
      setOrigin(originParam);
    }
  };

  useEffect(() => {
    updateOrigin();
  }, [search, originParam]);

  const trackMetrics = ({
    metric,
    action,
    featureId,
    flyerId,
    resource,
    resourceId,
    audienceId,
  }: Track) => {
    const context = {
      flyerId,
      districtId: resource === 'districts' ? resourceId.toString() : null,
      schoolId: audienceType === SubscriptionTypeEnum.district ? null : audienceId,
    };

    const data = {
      resource: metric,
      activity: 'clicked',
      source: METRICS_SOURCE_NAME,
      eventDate: Date.now(),
      userAgent: window.navigator.userAgent,
      context,
    };

    const v2MetricsData = {
      flyerIds: [flyerId],
      district: context.districtId,
      school: context.schoolId,
      action: action || metric,
      eventTime: moment().format(),
      source: METRICS_SOURCE_NAME,
      distributionId: 1,
      userAgent: window.navigator.userAgent,
      featureId,
      userLegacyId: user?.id,
      referrerUrl: document.referrer,
      origin,
    };
    postOne({ body: data });
    postMetrics({ body: v2MetricsData });
  };

  const trackImpressions = ({ flyers, districtId, flyerType, audienceId }: Impression) => {
    let context = {
      flyerIds: flyers.map(f => f.flyerId.toString()),
      schoolId: '',
    };

    if (
      flyerType === FlyerType.school &&
      !!audienceId &&
      audienceId !== 'all' &&
      audienceType !== SubscriptionTypeEnum.district
    ) {
      context.schoolId = audienceId.toString();
    }

    if (audienceType === SubscriptionTypeEnum.district) {
      context.schoolId = '';
    }

    const impressionsMetrics = {
      flyerIds: context.flyerIds,
      district: districtId,
      school: context.schoolId || null,
      eventTime: moment().format(),
      source: METRICS_SOURCE_NAME,
      action: context.schoolId ? 'flyerboard-school' : 'flyerboard-district',
      userLegacyId: user?.id,
      referrerUrl: document.referrer,
      origin,
    };

    postMetrics({ body: impressionsMetrics });
  };

  return { trackMetrics, trackImpressions };
};
